import React from 'react';
import { resolveCollectionCardRowGap } from '../collectionCardRendering';

type ProductCardRowsProps = {
  rowOrder: string[];
  template: any;
  measure?: boolean;
  productCardImageDomId?: string;
  productCardDescriptionDomId?: string;
  productCardImageFrameStyle: React.CSSProperties;
  productCardImageObjectPosition: string;
  productCardImageOverlayStyle?: React.CSSProperties;
  resolvedImage: string;
  resolvedImageSrc: string;
  resolvedImageAlt: string;
  resolvedName: string;
  resolvedDescription: string;
  titleTypographyStyle: React.CSSProperties;
  descriptionTypographyStyle: React.CSSProperties;
  isHoverActive: boolean;
  hoverAnimation: string;
  shouldRevealDescription: boolean;
};

export function ProductCardRows({
  rowOrder,
  template,
  measure = false,
  productCardImageDomId,
  productCardDescriptionDomId,
  productCardImageFrameStyle,
  productCardImageObjectPosition,
  productCardImageOverlayStyle,
  resolvedImage,
  resolvedImageSrc,
  resolvedImageAlt,
  resolvedName,
  resolvedDescription,
  titleTypographyStyle,
  descriptionTypographyStyle,
  isHoverActive,
  hoverAnimation,
  shouldRevealDescription,
}: ProductCardRowsProps) {
  return (
    <>
      {rowOrder.map((row, rowIndex) => {
        const previousRow = rowIndex > 0 ? rowOrder[rowIndex - 1] ?? null : null;
        const rowSpacingStyle =
          rowIndex > 0
            ? { marginTop: `${resolveCollectionCardRowGap(previousRow as any, row as any, template)}px` }
            : undefined;
        const key = measure ? `measure-${row}` : row;

        if (row === 'image') {
          return (
            <div
              key={key}
              data-builder-product-card-image-id={measure ? undefined : productCardImageDomId}
              className="relative w-full overflow-hidden rounded-md bg-stone-100"
              style={{
                ...productCardImageFrameStyle,
                ...rowSpacingStyle,
                transform:
                  !measure && isHoverActive && hoverAnimation === 'image-zoom'
                    ? `scale(${template.hoverImageZoomScale}) rotate(${template.hoverImageRotateDeg}deg)`
                    : 'scale(1) rotate(0deg)',
                transformOrigin: 'center center',
                transition: 'transform 180ms ease, opacity 180ms ease, filter 180ms ease',
              }}
            >
              {resolvedImage ? (
                template.imageAspect === 'original' ? (
                  <img
                    src={resolvedImageSrc}
                    alt={measure ? '' : resolvedImageAlt}
                    className="block h-auto w-full object-contain"
                    style={{ objectPosition: productCardImageObjectPosition }}
                    loading={measure ? 'eager' : 'lazy'}
                    decoding="async"
                    draggable={false}
                    onError={(event) => {
                      if (event.currentTarget.src === resolvedImage || !resolvedImage) return;
                      event.currentTarget.src = resolvedImage;
                    }}
                  />
                ) : (
                  <img
                    src={resolvedImageSrc}
                    alt={measure ? '' : resolvedImageAlt}
                    className="h-full w-full object-cover"
                    style={{ objectPosition: productCardImageObjectPosition }}
                    loading={measure ? 'eager' : 'lazy'}
                    decoding="async"
                    draggable={false}
                    onError={(event) => {
                      if (event.currentTarget.src === resolvedImage || !resolvedImage) return;
                      event.currentTarget.src = resolvedImage;
                    }}
                  />
                )
              ) : (
                <div className="flex h-full w-full items-center justify-center text-xs text-stone-500">
                  No image
                </div>
              )}
              {productCardImageOverlayStyle ? (
                <div className="pointer-events-none absolute inset-0" style={productCardImageOverlayStyle} />
              ) : null}
            </div>
          );
        }

        if (row === 'title') {
          return (
            <h3
              key={key}
              className="line-clamp-2 w-full text-stone-900"
              style={{ ...titleTypographyStyle, ...rowSpacingStyle }}
            >
              {resolvedName}
            </h3>
          );
        }

        if (row === 'description') {
          return (
            <p
              key={key}
              data-builder-product-card-description-id={measure ? undefined : productCardDescriptionDomId}
              className={`${measure ? 'w-full' : 'w-full flex-1'} text-stone-600`}
              style={{
                ...rowSpacingStyle,
                ...descriptionTypographyStyle,
                opacity: !measure && shouldRevealDescription && !isHoverActive ? 0 : 1,
                transform:
                  !measure && shouldRevealDescription && !isHoverActive
                    ? `translateY(${template.hoverRevealDistance}px)`
                    : 'translateY(0)',
                transition: 'transform 180ms ease, opacity 180ms ease, filter 180ms ease',
                display: '-webkit-box',
                WebkitLineClamp: template.descriptionLines,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {resolvedDescription}
            </p>
          );
        }

        return null;
      })}
    </>
  );
}
